import { usePrevious } from '@/hooks/usePrevious';
import { Loader } from '@/components/ui/loader';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChatSidebar, type ChatHistoryItemType, type ChatSidebarAction } from './ChatSidebar';

interface ChatContainerProps<
  TMessage extends { id: string },
  THistoryItem extends ChatHistoryItemType,
> {
  conversationsHeader?: ReactNode;
  conversations: Array<THistoryItem>;
  currentConversationMessages: Array<TMessage>;
  isLoadingMessages: boolean;
  welcomeMessage?: ReactNode;
  showInputArea?: boolean;
  chatInput: ReactNode;
  renderMessage: (message: TMessage, isLastMessage: boolean) => React.ReactNode;
  renderHistoryItem: (conversation: THistoryItem) => React.ReactNode;
  onNewConversation: () => void;
  sidebarActions?: ChatSidebarAction[];
  lastUserMessageTimestamp?: number;
  onLoadMore?: () => void;
  hasMoreMessages?: boolean;
  isLoadingMore?: boolean;
}

const isSmallScreenWidth = (): boolean => typeof window !== 'undefined' && window.innerWidth < 640;

export function ChatContainer<
  TMessage extends { id: string },
  THistoryItem extends ChatHistoryItemType,
>({
  conversationsHeader,
  conversations,
  currentConversationMessages,
  isLoadingMessages,
  welcomeMessage,
  showInputArea = true,
  renderMessage,
  renderHistoryItem,
  onNewConversation,
  chatInput,
  sidebarActions,
  lastUserMessageTimestamp,
  onLoadMore,
  hasMoreMessages = false,
  isLoadingMore = false,
}: ChatContainerProps<TMessage, THistoryItem>) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const previousMessages = usePrevious(currentConversationMessages);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(isSmallScreenWidth());
  const [isCollapsed, setIsCollapsed] = useState(isSmallScreenWidth());
  const scrollRequestRef = useRef<number | null>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const scrollOffsetBeforeLoad = useRef<number>(0);
  const lastLoadTime = useRef<number>(0);
  const previousMessageCount = useRef<number>(0);
  const containerScrollHeightBeforeLoad = useRef<number>(0);

  useEffect(() => {
    const handleResize = () => {
      const isSmall = isSmallScreenWidth();
      setIsSmallScreen(isSmall);
      if (isSmall && !isCollapsed) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  useEffect(() => {
    return () => {
      if (scrollRequestRef.current) {
        cancelAnimationFrame(scrollRequestRef.current);
      }
    };
  }, []);

  const scrollToBottom = useCallback((behavior: 'smooth' | 'instant' = 'smooth') => {
    if (scrollRequestRef.current) {
      cancelAnimationFrame(scrollRequestRef.current);
    }

    scrollRequestRef.current = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior });
      scrollRequestRef.current = null;
    });
  }, []);

  useLayoutEffect(() => {
    const endElement = messagesEndRef.current;
    const container = messagesContainerRef.current;

    if (!endElement || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAutoScrollEnabled(entry.isIntersecting);
      },
      {
        root: container,
        threshold: 0.1,
      }
    );

    observer.observe(endElement);
    return () => observer.disconnect();
  }, [messagesEndRef, messagesContainerRef]);

  useLayoutEffect(() => {
    const loadMoreTrigger = loadMoreTriggerRef.current;
    const container = messagesContainerRef.current;

    if (!loadMoreTrigger || !container || !onLoadMore || !hasMoreMessages || isLoadingMore) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: container,
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreTrigger);
    return () => observer.disconnect();
  }, [loadMoreTriggerRef, messagesContainerRef, onLoadMore, hasMoreMessages, isLoadingMore]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    const anchor = scrollAnchorRef.current;

    if (!container || !anchor) return;

    if (isLoadingMore) {
      containerScrollHeightBeforeLoad.current = container.scrollHeight;
      const anchorRect = anchor.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      scrollOffsetBeforeLoad.current = anchorRect.top - containerRect.top;
      previousMessageCount.current = currentConversationMessages.length;
    }
  }, [isLoadingMore, currentConversationMessages.length]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;

    if (!container || isLoadingMore) return;

    if (
      previousMessageCount.current > 0 &&
      currentConversationMessages.length > previousMessageCount.current &&
      containerScrollHeightBeforeLoad.current > 0
    ) {
      const scrollHeightDifference =
        container.scrollHeight - containerScrollHeightBeforeLoad.current;

      if (scrollHeightDifference > 0) {
        container.scrollTop += scrollHeightDifference;
      }

      previousMessageCount.current = 0;
      containerScrollHeightBeforeLoad.current = 0;
    }
  }, [currentConversationMessages.length, isLoadingMore]);

  useEffect(() => {
    if (lastUserMessageTimestamp) {
      scrollToBottom('instant');
    }
  }, [lastUserMessageTimestamp, scrollToBottom]);

  useEffect(() => {
    if (currentConversationMessages.length === 0) {
      lastLoadTime.current = 0;
      previousMessageCount.current = 0;
      containerScrollHeightBeforeLoad.current = 0;
    }
  }, [currentConversationMessages.length]);

  useEffect(() => {
    if (!previousMessages) return;

    const messageCountChanged = previousMessages.length !== currentConversationMessages.length;
    const isNewConversation =
      previousMessages.length === 0 && currentConversationMessages.length > 0;

    if (isNewConversation) {
      scrollToBottom('instant');
      setIsAutoScrollEnabled(true);
    } else if (isAutoScrollEnabled) {
      if (messageCountChanged) {
        scrollToBottom();
      } else if (currentConversationMessages.length > 0) {
        const lastMessage = currentConversationMessages[currentConversationMessages.length - 1];
        const prevLastMessage = previousMessages[previousMessages.length - 1];

        if (lastMessage?.id === prevLastMessage?.id) {
          scrollToBottom();
        }
      }
    }
  }, [currentConversationMessages, previousMessages, isAutoScrollEnabled, scrollToBottom]);

  const shouldHideMainContent = isSmallScreen && !isCollapsed;

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <ChatSidebar
        conversations={conversations}
        renderHistoryItem={renderHistoryItem}
        onNewConversation={onNewConversation}
        actions={sidebarActions}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />

      {/* Main Chat Area */}
      {!shouldHideMainContent && (
        <div className="flex-1 min-w-0 flex flex-col relative">
          {conversationsHeader}

          {/* Messages Area */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-5 pb-32">
            {welcomeMessage ? (
              <div className="flex flex-col h-full">
                {/* Welcome Message */}
                {welcomeMessage}
              </div>
            ) : (
              <div className="flex flex-col min-h-full">
                {/* Load more trigger (invisible, at top) */}
                {hasMoreMessages && !isLoadingMessages && !isLoadingMore && (
                  <div ref={loadMoreTriggerRef} className="h-1" aria-label="load-more-trigger" />
                )}
                {hasMoreMessages && (
                  <div className="flex items-center justify-center py-8">
                    <Loader />
                  </div>
                )}

                {/* Scroll anchor - invisible, marks the position to maintain */}
                <div ref={scrollAnchorRef} className="h-0" />

                {/* Messages */}
                {currentConversationMessages.map((message, index) =>
                  renderMessage(message, index === currentConversationMessages.length - 1)
                )}

                {/* Loading indicator */}
                {isLoadingMessages && (
                  <div className="flex-1 flex items-center justify-center min-h-0">
                    <Loader />
                  </div>
                )}

                {/* Invisible div for auto-scrolling */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area - Absolutely positioned */}
          {showInputArea && (
            <div className="absolute bottom-0 left-0 right-0 mx-5 pt-10 pb-5 flex justify-center bg-gradient-to-t from-white via-white/90 via-white/70 via-white/40 to-transparent">
              {chatInput}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
